import axiosClient from "@/shared/lib/axios/axios.ts";

/**
 * Minimal client for the ONE endpoint the live channel needs.
 *
 * Deliberately standalone: it does not go through the alerts-workspace envelope
 * / contract layer, so the map's live channel carries no dependency on the
 * queue, history, analytics or actions surfaces. The response shape is declared
 * here because it is the only thing this call returns.
 */
export interface LiveChannelCredentials {
  /** Short-lived, location-bound custom token. Never cached or persisted. */
  firebase_custom_token: string;
  /**
   * The project the token belongs to. The web config MUST match this or we
   * refuse to sign in — a mismatch means the token is for a different project
   * and would fail (or worse, silently listen to the wrong data).
   */
  firebase_project_id: string;
  events_collection_path: string;
  schema_version: number;
}

/** Thrown so callers can distinguish "channel unavailable" from a real bug. */
export class LiveChannelError extends Error {
  readonly status?: number;
  readonly code?: string;

  constructor(message: string, options: { status?: number; code?: string } = {}) {
    super(message);
    this.name = "LiveChannelError";
    this.status = options.status;
    this.code = options.code;
  }
}

const isAuthFailure = (status?: number) => status === 401 || status === 403;

/** Whether this failure means the session's claims are dead (stop the listener). */
export const isLiveChannelAuthError = (error: unknown): boolean =>
  error instanceof LiveChannelError && isAuthFailure(error.status);

/**
 * Requests a short-lived, location-bound custom token.
 *
 * A plain request rather than a React Query hook ON PURPOSE: neither the token
 * nor the response may be cached or persisted.
 */
export async function requestLiveChannelCredentials(
  locationId: number,
): Promise<LiveChannelCredentials> {
  try {
    const response = await axiosClient.post("/api/alerts/live/subscribe/", {
      location_id: locationId,
    });
    // The API wraps payloads as { metadata, data, error }; a 200 carrying an
    // `error` is still a failure.
    const body = response.data as {
      data?: LiveChannelCredentials | null;
      error?: { detail?: string; code?: string } | null;
    };
    if (body?.error) {
      throw new LiveChannelError(body.error.detail ?? "Live channel unavailable", {
        code: body.error.code,
        status: response.status,
      });
    }
    if (!body?.data?.firebase_custom_token) {
      throw new LiveChannelError("Live channel returned no token", {
        code: "live_channel_unavailable",
      });
    }
    return body.data;
  } catch (error) {
    if (error instanceof LiveChannelError) throw error;
    const axiosLike = error as {
      response?: { status?: number; data?: { error?: { detail?: string; code?: string } } };
      message?: string;
    };
    throw new LiveChannelError(
      axiosLike.response?.data?.error?.detail ?? axiosLike.message ?? "Live channel unavailable",
      {
        status: axiosLike.response?.status,
        code: axiosLike.response?.data?.error?.code ?? "live_channel_unavailable",
      },
    );
  }
}
